import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AdminUserEditModel } from '../../shared/models';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomSplitCamelCasePipe, CustomValidators } from 'app/shared';
import { trigger } from 'devextreme/events';
import { Router } from '@angular/router';
import { ModalComponent } from 'app/internal/shared';
import { UserProfile } from 'app/internal/user/models/user-profile';
import { AtlasSubscriptionType } from 'app/internal/shared/models/workspace-user/workspace-user';

@Component({
  selector: 'sp-admin-user-edit',
  templateUrl: './admin-user-edit.component.html',
  styleUrls: ['./admin-user-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminUserEditComponent implements OnChanges {

  @ViewChild('deactivateUserModal', { static: true }) public deactivateUserModal: ModalComponent;
  @ViewChild('reactivateUserModal', { static: true }) public reactivateUserModal: ModalComponent;

  @Input() user: AdminUserEditModel;
  @Input() userProfile: UserProfile;
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<AdminUserEditModel>();

  isSubmitting = false;
  formGroup: UntypedFormGroup;
  toggleStatusUserId: string;
  toggleStatusWorkspaceId: string;
  atlasSubscriptionTypes: { text: string; value: number }[] = [];

  constructor(private fb: UntypedFormBuilder, private router: Router) {
    this.atlasSubscriptionTypes = this.buildAtlasSubscriptionTypes();
    this.atlasSubscriptionTypes.unshift({ value: null, text: 'None' });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    if (this.user) {
      this.initForm(this.user);
    }
  }

  dxGridRowPrepared(e) {
    if (e.rowType === 'data') {
      e.rowElement.style.height = '40px';
    }
  }

  saveUser() {
    this.isSubmitting = true;

    const user = <AdminUserEditModel>{
      userId: this.user.userId,
      firstName: this.formGroup.get('firstName').value,
      lastName: this.formGroup.get('lastName').value,
      email: this.formGroup.get('email').value,
      atlasSubscriptionTypeId: this.formGroup.get('atlasSubscriptionType').value
    };

    this.save.emit(user);
    this.isSubmitting = false;
    this.resetForm();
  }

  cancelChanges() {
    this.cancel.emit();
  }

  showActionMenu(e) {
    // This allows us to either left or right click to show the menu.
    const newEvent = Object.assign({}, e.event, { type: 'dxcontextmenu' });
    e.event.stopPropagation();
    trigger(e.element, newEvent);
  }

  onContextMenuPreparing(e) {

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // Only show the Context Menu if the Menu Button is clicked!
    if (e.row.rowType === 'data' && e.column.cellTemplate === 'actionMenu') {

      const userId = that.user.userId;
      const workspaceId = e.row.data.workspaceId;

      e.items = [
        {
          text: 'Edit User',
          onItemClick: function () {
            that.router.navigate(['/useraccount/admin/portfolios/' + workspaceId + '/edit-user/' + userId]);
          }
        },
        {
          text: 'Edit Portfolio',
          onItemClick: function () {
            that.router.navigate(['/useraccount/admin/portfolios/' + workspaceId]);
          }
        }
      ];

      if (e.row.data.propertyId) {

        const propertyId = e.row.data.propertyId;

        e.items.push(
          {
            text: 'Edit Property',
            onItemClick: function () {
              that.router.navigate(['/useraccount/admin/portfolios/' + workspaceId + '/edit-property/' + propertyId]);
            }
          }
        );
      }
    }
  }

  dxGetPortfolioNameForPortfolioGrid(rowData: any) {
    if (rowData.companyName && rowData.companyName !== '') {
      return rowData.companyName;
    } else {
      return rowData.name;
    }
  }

  dxGetPortfolioNameForPropertyGrid(rowData: any) {
    if (rowData.workspaceCompanyName && rowData.workspaceCompanyName !== '') {
      return rowData.workspaceCompanyName;
    } else {
      return rowData.workspaceName;
    }
  }

  private initForm(user: AdminUserEditModel) {
    this.formGroup = this.fb.group({});
    this.formGroup.addControl(
      'firstName',
      this.fb.control(user?.firstName,
        [
          Validators.required,
        ])
    );
    this.formGroup.addControl(
      'lastName',
      this.fb.control(user?.lastName,
        [
          Validators.required,
        ])
    );
    this.formGroup.addControl(
      'email',
      this.fb.control(user?.email,
        [
          Validators.required,
          CustomValidators.emailValidator
        ])
    );
    this.formGroup.addControl(
      'atlasSubscriptionType',
      this.fb.control(user?.atlasSubscriptionTypeId)
    );
  }

  private resetForm() {
    this.formGroup.reset();
  }

  private buildAtlasSubscriptionTypes(): { text: string; value: number }[] {
    const camelCasePipe = new CustomSplitCamelCasePipe();
    return Object.entries(AtlasSubscriptionType)
      .filter(enumVal => !isNaN(enumVal[0] as any))
      .map(enumVal => ({
        text: camelCasePipe.transform(enumVal[1].toString()),
        value: +enumVal[0]
      }));
  }
}
